import join from 'lodash/join';
import unescape from 'lodash/unescape';
import moment from 'moment';
import type { ProcessCellForExportParams } from '@ag-grid-community/core';

import { I18n } from 'common/i18n';
import type { ViewColumn } from 'common/types/viewColumn';
import { SoQLType } from 'common/types/soql';
import { GROUP_COLUMN_PREFIX, NULL_GROUPING, PIVOT_SEPARATOR } from '../Constants';

import { unsupportedDateFormats } from './TableExportFormatting';
import { getFieldName } from './getFieldName';
import { isSubtotalCell, isTotalCell } from './isTotalCell';
import { isColDefPivoted } from './isColDefPivoted';

export const processCellCallback = (params: ProcessCellForExportParams) => {
  if (isCellExportEmpty(params)) {
    return '';
  }
  const renderer = exportRenderer(params);
  return exportReturnValues(renderer, params);
};

const isCellExportEmpty = (params: ProcessCellForExportParams) => {
  if (isTotalCell(params)) {
    return false;
  }
  const isValueUndefined = params.value === undefined;
  const isNullGrouping = params.node?.key === NULL_GROUPING && isValueUndefined;
  return !params.node || skipSubtotalHeader(params) || isNullGrouping || isValueUndefined;
};

const skipSubtotalHeader = (params: ProcessCellForExportParams) => {
  const fieldName = getFieldName(params);
  if (!fieldName || isColDefPivoted(params.column.getColDef())) {
    return false;
  }

  const { groupData, footer, level } = params.node!;
  const { groupedColumns, showSubTotal } = params.context;

  return (
    groupData &&
    !footer &&
    level < groupedColumns.length &&
    !groupedColumns.includes(fieldName) &&
    showSubTotal
  );
};

// If colDef.cellRenderer is a string, it means that the column is a group, and we should use the grouping renderer, which is:
// common/visualizations/views/agGridReact/customHooks/useAutoGroupAttributes.ts:L161
// Reference:
// https://www.ag-grid.com/javascript-data-grid/group-cell-renderer/#configuration
const exportRenderer = (params: ProcessCellForExportParams) => {
  const colDef = params.column.getColDef();

  // When exporting pivoted columns, AG Grid does not set params.value for total rows. Instead AgGrid
  // sends params.value as undefined and field as something like, 'pivot_year_2019_grade'. We need to
  // manually extract the total value from params.node.data using the constructed field name and set
  // params.value to that.
  if (isColDefPivoted(colDef) && params.value === undefined) {
    const pivotColumns = colDef?.pivotKeys?.join(PIVOT_SEPARATOR);
    const valueColumn = colDef?.pivotValueColumn?.getColId();
    if (pivotColumns && valueColumn) {
      const name = `${pivotColumns}${PIVOT_SEPARATOR}${valueColumn}`;
      params.value = params.node?.data ? params.node.data[name] : undefined;
    }
  }

  const rendererParams = {
    value: params.value,
    context: params.context,
    colDef: colDef,
    data: params.node?.data,
    node: params.node,
    forExport: true,
  };
  return typeof colDef.cellRenderer === 'string'
    ? colDef.cellRendererParams.innerRenderer(rendererParams)
    : colDef.cellRenderer(rendererParams);
};

const exportReturnValues = (
  renderer: string | { props: { children: string | string[] } },
  params: ProcessCellForExportParams,
) => {
  const field = getFieldName(params);
  let formattedValue = typeof renderer === 'string' ? renderer : renderer.props.children;
  // params.value for a non-subtotal cell is the unformatted value,
  // whereas params.value for subtotals is the value being passed from processRowGroupCallback() (including the subtotal label)
  if (isURL(params, field)) {
    formattedValue = getUrlValue(params, field);
  }
  // we expect to display the email value as text in Excel, instead of the formatted value with anchor tags.
  if (isEmail(params)) {
    formattedValue = params.value;
  }

  if (isTotalCell(params) || isSubtotalCell(params)) {
    // If field and params.node.field are equal, this is a subtotal hierarchy cell and should be appended with the subtotal label.
    // Otherwise, the cell contains an aggregation value.
    const withSubtotalLabel = field === params.node?.field;
    const subtotalReturnValue = withSubtotalLabel
      ? `${I18n.t('shared.visualizations.charts.table.subtotal')} ${formattedValue}`
      : formattedValue;

    // If the subtotal/total cell contains purely just date value (does not include cells with 'Subtotal <date-value>'),
    // return the value right away, no need to format it
    const isDateCellForTotalOrSubtotal =
      !withSubtotalLabel && isDate(params, field) && moment(params.value, moment.ISO_8601, true).isValid();
    if (isDateCellForTotalOrSubtotal) {
      return params.value;
    }

    if (!withSubtotalLabel && isNumber(params, field)) {
      return params.value;
    }

    // There are instances where formattedValue is an array, e.g., ['Subtotal', '', '$100'].
    // If formattedValue is a string and represents a subtotal, this is most likely a column aggregation value,
    // and we also want to display the formattedValue.
    return typeof formattedValue == 'string' ? subtotalReturnValue : join(formattedValue, '');
  }

  if (isDate(params, field)) {
    const dateViewFormat = params.context.columnFormats[field as string]?.format.view as string;
    if (!unsupportedDateFormats.includes(dateViewFormat)) {
      formattedValue = params.value;
    }
  }

  if (isNumber(params, field)) {
    formattedValue = params.value ?? '';
  }

  const dataTypeName = getDatatypeName(params.context.columnMetadata, field);
  if (dataTypeName === 'text' && typeof formattedValue == 'string') {
    return unescape(formattedValue);
  }
  return formattedValue;
};

const getUrlValue = (params: ProcessCellForExportParams, field: string | undefined | boolean) => {
  if (!params.value && params.node?.field === field) {
    return I18n.t('shared.visualizations.charts.common.no_value');
  }
  return params.value;
};

const isEmail = (params: ProcessCellForExportParams) => {
  const colDef = params.column.getColDef();
  let fieldName = colDef.field ?? colDef.colId;
  fieldName = fieldName?.replace(`${GROUP_COLUMN_PREFIX}-`, '');
  return (
    params.context.columnMetadata.find((column: ViewColumn) => column.fieldName === fieldName)?.format
      ?.displayStyle === 'email'
  );
};

const isURL = (params: ProcessCellForExportParams, field: boolean | string | undefined) => {
  const dataTypeName = getDatatypeName(params.context.columnMetadata, field);
  return dataTypeName == 'url';
};

const isDate = (params: ProcessCellForExportParams, field: boolean | string | undefined) => {
  const dataTypeName = getDatatypeName(params.context.columnMetadata, field);
  return dataTypeName == SoQLType.SoQLFloatingTimestampT;
};

const isNumber = (params: ProcessCellForExportParams, field: boolean | string | undefined) => {
  const dataTypeName = getDatatypeName(params.context.columnMetadata, field);
  return dataTypeName == SoQLType.SoQLNumberT;
};

const getDatatypeName = (columnMetadata: ViewColumn[], field: boolean | string | undefined) => {
  return columnMetadata.find((column: ViewColumn) => column.fieldName == field)?.dataTypeName;
};
